import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import {
  AppointmentEventPlace,
  BindPlanningRequest,
  BookPlanningRequest,
  PersonalDoctorOption,
  PlanningCandidateKind,
  PlanningCandidateModel,
  PlanningPrescriptionDetails,
  PlanningSource,
  PlanningSuggestionModel,
  PrescriptionBaselinesService,
  PrescriptionLineView,
  PrescriptionPlanningKind,
  RecordPlanningRequest,
  SearchArea,
  ServiceKind,
  eventPlaceOf,
  toDateInput,
} from '@lib/guarantees/data-access';
import { bindPlanning, bookPlanning, recordPlanning } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { SERVICE_KIND_ICON, SERVICE_KIND_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { BOOKING_BLOCKER_HINT_KEY, bookingBlocker, partnerServiceFor } from '@lib/guarantees/feature/components/common/prescription-fulfillment';
import { Action } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, catchError, of, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../components/common/callout.component';
import { CheckBoxComponent } from '../components/common/check-box.component';
import { ControlDirective } from '../components/common/control.directive';
import { FieldLabelComponent } from '../components/common/field-label.component';
import { franchiseLimit } from '../components/common/package-option-picker.component';
import { SpinnerComponent } from '../components/common/spinner.component';
import { StepNavComponent, StepNavEntry } from '../components/common/step-nav.component';
import { ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES } from '../components/ui/ui.classes';
import { BasketChoice, BasketPickerComponent } from './basket-picker.component';
import { CandidatePickerComponent } from './candidate-picker.component';
import { DoctorPickerComponent } from './doctor-picker.component';
import { PlaceChoice, PlacePickerComponent } from './place-picker.component';
import { SlotChoice, SlotPickerComponent, SlotSearchKind } from './slot-picker.component';

/**
 * What is being arranged, which follows from the service and decides the shape of the record. The
 * planning families minus the base one: every arrangement this flow makes is one of the three.
 */
export type BookingShape = Exclude<PrescriptionPlanningKind, PrescriptionPlanningKind.Planning>;

/**
 * How the arrangement came to exist.
 *
 * `bind` names one the member already holds, `manual` records one made off-system, and `auto` is
 * the one we place ourselves through the partner's integration.
 */
export type ExecutionSource = 'bind' | 'manual' | 'auto';

/** The three steps, in the order an arrangement is actually settled. */
export const EXECUTION_STEPS = ['source', 'order', 'terms'] as const;

export type ExecutionStep = (typeof EXECUTION_STEPS)[number];

export interface ExecutionFormData {
  /** Whose record this is. What the booking says about itself is read off the line, not off here. */
  memberId: string;
  line: PrescriptionLineView;
  /** Lines the same arrangement could also deliver: same guarantee, same service, nothing booked. */
  siblings: PrescriptionLineView[];
}

/**
 * What the flow answers with, and it is three different acts rather than one with a flag.
 *
 * `bind` arranges the delivery for real against a booking the member already holds: that booking is
 * named, the lines are planned against it, and the member is given the task that carries it. That
 * correlation is what the act makes — nothing holds it beforehand.
 *
 * `book` arranges it for real with nothing held beforehand: an opening the partner published is
 * taken, and the visit or the order is placed in the partner's own system.
 *
 * `record` writes down an arrangement made by phone. Nothing reaches the partner, but the gateway
 * still makes a record of our own for it and raises the member's task, as it does for the other two.
 */
export type ExecutionFormResult = {
  prescriptionIds: string[];
  /** Lines to stop checking a contract for, where none was in force when the care happened. */
  considerPaidIds: string[];
} & ({ mode: 'bind'; binding: BindPlanningRequest } | { mode: 'book'; booking: BookPlanningRequest } | { mode: 'record'; record: RecordPlanningRequest });

/** A line one arrangement could also deliver, with the reason it cannot where there is one. */
interface SiblingRow {
  line: PrescriptionLineView;
  /** Why this line cannot be part of a real arrangement, or empty where it can. */
  blockerKey: string;
}

/** One covered line as the terms step prices it. */
interface PriceRow {
  id: string;
  name: string;
  code?: string;
  price: number | null | undefined;
}

/** The arrangement as the last step reads it back, however it was answered. */
interface ArrangementSummary {
  partnerName?: string;
  place?: string;
  doctorName?: string;
  startsAt?: string;
  /** Where the arrangement runs to, when that is a different moment from where it starts. */
  endsAt?: string;
  orderNumber?: string;
}

/**
 * An end worth stating beside the start, which means one on the same day.
 *
 * The summary names the end by its clock alone, so an end on another day would read as an hour that
 * evening. A pharmacy order collected over the weeks a course runs has exactly such an end — the
 * record keeps it, and this row is not where it is read.
 */
function spanEnd(startsAt: string | undefined, endsAt: string | undefined): string | undefined {
  if (!startsAt || !endsAt || endsAt === startsAt) {
    return undefined;
  }

  return new Date(startsAt).toDateString() === new Date(endsAt).toDateString() ? endsAt : undefined;
}

interface SourceOption {
  id: ExecutionSource;
  icon: string;
  labelKey: string;
  hintKey: string;
}

const BOOKING_SHAPE: Record<ServiceKind, BookingShape> = {
  [ServiceKind.Appointment]: PrescriptionPlanningKind.Appointment,
  [ServiceKind.PersonalDoctorAppointment]: PrescriptionPlanningKind.Appointment,
  [ServiceKind.InstrumentalDiagnostic]: PrescriptionPlanningKind.Appointment,
  [ServiceKind.LaboratoryTest]: PrescriptionPlanningKind.Laboratory,
  [ServiceKind.Medication]: PrescriptionPlanningKind.Drugs,
  [ServiceKind.Other]: PrescriptionPlanningKind.Appointment,
  [ServiceKind.Unknown]: PrescriptionPlanningKind.Appointment,
};

/** Which of the member's own records answers this shape of arrangement. */
const CANDIDATE_KIND: Record<BookingShape, PlanningCandidateKind> = {
  [PrescriptionPlanningKind.Appointment]: PlanningCandidateKind.Appointment,
  [PrescriptionPlanningKind.Laboratory]: PlanningCandidateKind.Laboratory,
  [PrescriptionPlanningKind.Drugs]: PlanningCandidateKind.Drugs,
};

export const EXECUTION_STEP_LABEL_KEY: Record<ExecutionStep, string> = {
  source: 'guaranteeV2.label.stepSource',
  order: 'guaranteeV2.label.stepOrder',
  terms: 'guaranteeV2.label.stepTerms',
};

export const EXECUTION_STEP_HINT_KEY: Record<ExecutionStep, string> = {
  source: 'guaranteeV2.message.stepSourceHint',
  order: 'guaranteeV2.message.stepOrderHint',
  terms: 'guaranteeV2.message.stepTermsHint',
};

/** The service category that makes a line a family doctor's, as the dictionary names it. */
const PERSONAL_DOCTOR_CATEGORY = 'PersonalDoctor';

/** What a visit is booked for when nothing says otherwise. Long enough to be plausible, short enough to correct. */
const DEFAULT_MINUTES = 30;

/**
 * Arranges the delivery of a prescribed service, and answers with the record to write.
 *
 * A named front door rather than a shape each caller assembles: the siblings one booking may also
 * cover are part of the question, so no call site is in a position to leave them out.
 */
export function openExecutionForm(dialog: MatDialog, data: ExecutionFormData): Observable<ExecutionFormResult | undefined> {
  return dialog
    .open<ExecutionFormComponent, ExecutionFormData, ExecutionFormResult>(ExecutionFormComponent, {
      width: '90vw',
      maxWidth: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    })
    .afterClosed()
    .pipe(take(1));
}

/**
 * The write the flow's answer calls for. Held beside the form rather than at each call site: the
 * three acts go to different routes, and a screen that reached for the wrong one would look like it
 * had saved while nothing was booked.
 */
export function planningActionFor(guaranteeId: string, result: ExecutionFormResult): Action {
  const considerPaidIds = result.considerPaidIds;

  switch (result.mode) {
    case 'bind':
      return bindPlanning({ guaranteeId, request: result.binding, considerPaidIds });
    case 'book':
      return bookPlanning({ guaranteeId, request: result.booking, considerPaidIds });
    case 'record':
      return recordPlanning({ guaranteeId, request: result.record, considerPaidIds });
  }
}

/**
 * Arranges the delivery of a prescribed service: how it came about, what it delivers and where, and
 * on what terms.
 *
 * One flow for all three shapes, because the manager answers the same three questions of each. The
 * first is what separates the cases. An arrangement already held is named, which takes the place,
 * the time and the terms off it whole. One made by phone is described, and recorded without the
 * partner hearing of it. One placed through the partner's integration is made here and now — the
 * manager takes an opening the partner published, or a pharmacy that has the drugs on the shelf,
 * exactly as the member would in their own app.
 *
 * All three need the member's outstanding task for the line, since that is what the arrangement
 * hangs off and what carries it to the member.
 */
@Component({
  selector: 'guarantees-execution-form',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    BasketPickerComponent,
    CandidatePickerComponent,
    DoctorPickerComponent,
    PlacePickerComponent,
    SlotPickerComponent,
    ControlDirective,
    CalloutComponent,
    CheckBoxComponent,
    FieldLabelComponent,
    SpinnerComponent,
    StepNavComponent,
    ToggleDirective,
  ],
  // Passes the dialog's height cap down to the shell, so the body scrolls and the footer stays in view.
  host: { class: 'block max-h-[inherit]' },
  templateUrl: './execution-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExecutionFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  private readonly dialogRef = inject(MatDialogRef<ExecutionFormComponent, ExecutionFormResult>);

  protected readonly data = inject<ExecutionFormData>(MAT_DIALOG_DATA);

  protected readonly kindIcon = SERVICE_KIND_ICON;
  protected readonly kindLabelKey = SERVICE_KIND_LABEL_KEY;
  protected readonly stepHintKey = EXECUTION_STEP_HINT_KEY;

  protected readonly prescription = this.data.line.prescription;
  protected readonly shape: BookingShape = BOOKING_SHAPE[this.prescription.kind] ?? PrescriptionPlanningKind.Appointment;
  protected readonly partnerService = partnerServiceFor(this.prescription.kind);

  protected readonly candidateKind = CANDIDATE_KIND[this.shape];

  /** A visit is delivered by a doctor; an order is placed with the partner and nobody in particular. */
  protected readonly withDoctor = this.shape === PrescriptionPlanningKind.Appointment;

  /** A collection has a day rather than a slot, so an end time would be a number nobody knows. */
  protected readonly withEndTime = this.shape !== PrescriptionPlanningKind.Drugs;

  /** Drugs are arranged by what is on the shelf rather than by an hour, so they have no timetable. */
  protected readonly ordersDrugs = this.shape === PrescriptionPlanningKind.Drugs;

  protected readonly slotKind: SlotSearchKind = this.shape === PrescriptionPlanningKind.Laboratory ? 'laboratory' : 'appointment';

  /**
   * A family doctor is searched for differently on the partner side — integrated partners only, and
   * no branch without a free hour — so what an empty list means differs too.
   */
  /**
   * Whether this line is a family doctor's, which the service code settles and the kind does not.
   *
   * The kind is picked by hand while authoring and is routinely left on «Консультація» for a line
   * whose code is a family doctor's service. The gateway reads the code's category and nothing else
   * when it decides which schedule search to run, so this reads the same thing the same way: asking
   * a different question here would have the screen offer one flow while the partner answered the
   * other.
   */
  protected readonly familyDoctor = signal(false);

  /** False only while the dictionary is being asked, so nothing is searched on a guess. */
  protected readonly kindResolved = signal(true);

  /**
   * Where the line says it must be delivered, settled when it was authored. Nothing enforces it, so
   * honouring it is this screen's job: it narrows what the partner is asked for rather than letting
   * the manager pick past it.
   */
  protected readonly restriction = this.prescription.restriction;

  /** Named by the line, or chosen here — a line that already says who leaves nothing to choose. */
  protected readonly searchDoctorId = computed(() => this.restriction?.doctorId ?? this.doctor()?.id);
  protected readonly searchPartnerId = computed(() => this.restriction?.partnerId ?? this.doctor()?.partnerId);

  /** Asked only where the line has not already answered it. */
  protected readonly needsDoctorStep = computed(() => this.familyDoctor() && !this.restriction?.doctorId && !this.doctor());

  private readonly dictionaries = inject(DictionariesApiService);
  private readonly baselines = inject(PrescriptionBaselinesService);

  constructor() {
    this.loadPrices();

    // Only a visit has the question at all, and only a coded line can answer it: a free-form line
    // carries no code, which is exactly what the gateway resolves to an ordinary search.
    if (this.shape !== PrescriptionPlanningKind.Appointment || !this.prescription.code) {
      return;
    }

    this.kindResolved.set(false);

    this.dictionaries
      .searchAchiCodes({ searchTerm: '', pageSize: 1, code: this.prescription.code, category: PERSONAL_DOCTOR_CATEGORY })
      .pipe(
        catchError(() => of({ items: [] })),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        this.familyDoctor.set((result.items ?? []).length > 0);
        this.kindResolved.set(true);
      });
  }

  /**
   * What each offering is listed at, for every line authored without a figure of its own.
   *
   * The amount set aside for a line is the answer whenever it has one — it is what somebody agreed
   * the line was worth. The price list only ever fills a blank, and never one the manager has since
   * written in or cleared, so a figure cleared on purpose stays cleared.
   */
  private loadPrices(): void {
    for (const line of this.lines) {
      const id = line.prescription.id;
      const code = line.prescription.code?.trim();
      if (!code || this.priceOf(id) !== undefined) {
        continue;
      }

      this.baselines
        .maxPrice(code)
        .pipe(
          take(1),
          catchError(() => of(undefined)),
          takeUntilDestroyed()
        )
        .subscribe(price => {
          if (price != null && this.priceOf(id) === undefined) {
            this.setPrice(id, round(price));
          }
        });
    }
  }

  protected readonly sources: SourceOption[] = [
    {
      id: 'bind',
      icon: 'link',
      labelKey: 'guaranteeV2.action.sourceBind',
      hintKey: this.withDoctor ? 'guaranteeV2.message.sourceBindVisitHint' : 'guaranteeV2.message.sourceBindOrderHint',
    },
    { id: 'manual', icon: 'edit_calendar', labelKey: 'guaranteeV2.action.sourceManual', hintKey: 'guaranteeV2.message.sourceManualHint' },
    { id: 'auto', icon: 'bolt', labelKey: 'guaranteeV2.action.sourceAuto', hintKey: 'guaranteeV2.message.sourceAutoHint' },
  ];

  protected readonly source = signal<ExecutionSource>('bind');

  /** The booking already held that these lines are to be delivered by, where one was named. */
  protected readonly candidate = signal<PlanningCandidateModel | undefined>(undefined);

  /**
   * A visit held only in the partner's own system, where the member was booked there directly.
   * Taking it makes our record for that visit rather than binding one we already have.
   */
  protected readonly suggestion = signal<PlanningSuggestionModel | undefined>(undefined);

  /** The partner and branch the partner's visits were asked of, kept across steps like the area. */
  protected readonly suggestionPlace = signal<PlaceChoice | undefined>(undefined);

  /** Only a visit is held on the partner's side in a form we can find again and take over. */
  protected readonly offersSuggestions = this.shape === PrescriptionPlanningKind.Appointment;

  /**
   * The family doctor the visit is to be with, where the line calls for one.
   *
   * Settled before any hour is: the partner's schedule refuses to answer without a doctor named, and
   * the member's own app asks the same two questions in the same order.
   */
  protected readonly doctor = signal<PersonalDoctorOption | undefined>(undefined);

  /** The opening taken from what the partner publishes, where one was. */
  protected readonly slot = signal<SlotChoice | undefined>(undefined);

  /** The pharmacy to order from and what it is to put in the order. */
  protected readonly basket = signal<BasketChoice | undefined>(undefined);

  protected readonly place = signal<PlaceChoice | undefined>(undefined);

  /**
   * Where the partner search is centred, held by the form rather than by the picker that asks for it.
   *
   * Both pickers are built and torn down with the place step, and the place is a question the
   * manager answered once — stepping forward to the terms and back again should not ask it twice.
   */
  protected readonly area = signal<SearchArea | undefined>(undefined);

  protected readonly date = signal(toDateInput(this.prescription.details?.startDate));
  protected readonly startTime = signal('09:00');
  protected readonly endTime = signal(addMinutes('09:00', DEFAULT_MINUTES));

  /** Every line this arrangement could deliver, the opened one first. */
  private readonly lines: PrescriptionLineView[] = [this.data.line, ...this.data.siblings];

  /**
   * What each line costs, by prescription id. What was set aside for a line is nearly always what
   * the booking costs, so it is the default; a line the manager cleared is kept as `null` so it is
   * not filled back in.
   */
  private readonly linePrices = signal<Record<string, number | null>>(
    Object.fromEntries(this.lines.filter(line => line.prescription.financials?.reserved !== undefined).map(line => [line.prescription.id, line.prescription.financials!.reserved!]))
  );

  /** The line's own franchise, else the one the sub-option it was guaranteed under states. */
  protected readonly franchise = signal<number | undefined>(
    this.prescription.financials?.coverageFranchise ?? this.prescription.coverage?.options?.[0]?.subOptions?.map(sub => franchiseLimit(sub.limits)).find(value => value != null)
  );
  protected readonly orderNumber = signal('');

  /** The line the flow was opened from is always delivered by what it arranges. */
  protected readonly coveredIds = signal<string[]>([this.prescription.id]);

  protected readonly considerPaid = signal(false);

  /** Offered only where it changes something: a contract in force is what would otherwise be checked. */
  protected readonly offersConsiderPaid = !this.data.line.contractInForce;

  /** Naming a booking already held answers the place; everything else describes or places one. */
  protected readonly binding = computed(() => this.source() === 'bind');

  /** Placing it ourselves, through the partner's own timetable or shelf. */
  protected readonly booking = computed(() => this.source() === 'auto');

  /** Made by phone: described here and recorded, with nothing sent to the partner. */
  protected readonly recording = computed(() => this.source() === 'manual');

  /**
   * Why this line cannot be arranged for real, where it cannot. Read once: it is a fact about where
   * the record and the line stand, and neither moves while this dialog is open.
   */
  private readonly blocker = bookingBlocker(this.data.line);

  /**
   * Named on the step that offers the act, rather than only on the button that refuses to run it.
   * Every act is held to it — the arrangement hangs off the member's task whichever way it is made.
   */
  protected readonly blockerKey = this.blocker ? BOOKING_BLOCKER_HINT_KEY[this.blocker] : '';

  /** The partner's own number for an order described by hand — one arranged for real carries its own. */
  protected readonly offersOrderNumber = computed(() => !this.withDoctor && this.recording());

  /**
   * The other lines the same arrangement could deliver, each with whatever stands in its way.
   *
   * The same rule the opened line is held to: delivery is arranged against a line the member has
   * been given a task for, and a line nobody has guaranteed has none. Offering it here would let it
   * in through a side door — so it is offered, refused, and said why, rather than quietly dropped.
   */
  protected readonly siblingRows: SiblingRow[] = this.data.siblings.map(line => {
    const blocker = bookingBlocker(line);

    return { line, blockerKey: blocker ? BOOKING_BLOCKER_HINT_KEY[blocker] : '' };
  });

  protected readonly step = signal<ExecutionStep>('source');

  protected readonly stepIndex = computed(() => EXECUTION_STEPS.indexOf(this.step()));
  protected readonly isFirst = computed(() => this.stepIndex() <= 0);
  protected readonly isLast = computed(() => this.stepIndex() === EXECUTION_STEPS.length - 1);

  /** The route as the nav reads it: what each step is called and whether it has been answered. */
  protected readonly stepEntries = computed<StepNavEntry[]>(() => EXECUTION_STEPS.map(id => ({ id, labelKey: EXECUTION_STEP_LABEL_KEY[id], completed: this.stepCompleted(id) })));

  /** The covered lines with what each costs, in the order they were taken. */
  protected readonly priceRows = computed<PriceRow[]>(() => {
    const prices = this.linePrices();

    return this.coveredIds().map(id => {
      const line = this.lines.find(candidate => candidate.prescription.id === id)!;

      return { id, name: line.prescription.name, code: line.prescription.code, price: prices[id] };
    });
  });

  /** What the arrangement costs as a whole, where any line has a figure. */
  protected readonly totalPrice = computed(() => {
    const priced = this.priceRows().filter(row => row.price != null);

    return priced.length > 0 ? round(priced.reduce((sum, row) => sum + row.price!, 0)) : undefined;
  });

  /** Every answer the record needs is in, whichever step the manager is standing on. */
  protected readonly canSubmit = computed(() => !this.blockerKey && this.placeAnswered() && this.termsAnswered() && this.coveredIds().length > 0);

  /**
   * What is still missing, named as the step that asks for it. A booking left unnamed is a different
   * thing to be missing than a place and a time typed in by hand.
   */
  protected readonly submitBlockerKey = computed(() => {
    if (this.blockerKey) {
      return this.blockerKey;
    }

    if (this.binding()) {
      return 'guaranteeV2.message.stepRecordRequired';
    }

    if (this.booking()) {
      if (this.ordersDrugs) {
        return 'guaranteeV2.message.stepPharmacyRequired';
      }

      return this.needsDoctorStep() ? 'guaranteeV2.message.familyDoctorFirst' : 'guaranteeV2.message.stepSlotRequired';
    }

    if (!this.placeAnswered()) {
      return this.withDoctor ? 'guaranteeV2.message.stepVisitPlaceRequired' : 'guaranteeV2.message.stepPlaceRequired';
    }

    return 'guaranteeV2.message.stepManualTermsRequired';
  });

  /**
   * What the arrangement will say, read off whichever answer settled it. Assembled in one place so
   * the last step states the arrangement rather than re-deriving it from three sets of fields.
   */
  protected readonly summary = computed<ArrangementSummary>(() => {
    if (this.binding()) {
      const suggested = this.suggestion();
      if (suggested) {
        return {
          partnerName: this.suggestionPlace()?.partnerName,
          place: suggested.locationName,
          doctorName: suggested.doctorName,
          startsAt: suggested.startDate,
          endsAt: spanEnd(suggested.startDate, suggested.endDate),
        };
      }

      const bound = this.candidate();

      return {
        partnerName: bound?.partnerName,
        place: bound?.locationName || bound?.locationAddress,
        doctorName: bound?.doctorName,
        startsAt: bound?.dateStart,
        endsAt: spanEnd(bound?.dateStart, bound?.dateEnd),
        orderNumber: bound?.orderNumber,
      };
    }

    if (this.booking()) {
      const pharmacy = this.basket()?.pharmacy;
      if (pharmacy) {
        return { partnerName: pharmacy.pharmacyName, place: pharmacy.pharmacyAddress, startsAt: this.collectionWindow().from };
      }

      const taken = this.slot();

      return {
        partnerName: taken?.offer.partnerName,
        place: taken?.offer.locationAddress || taken?.offer.locationName,
        doctorName: taken?.offer.doctorName,
        startsAt: taken?.slot.startTime,
        // A laboratory's opening is a window to come within rather than an hour to arrive at, so
        // the end of it is half of what was actually taken.
        endsAt: spanEnd(taken?.slot.startTime, taken?.slot.endTime),
      };
    }

    const place = this.place();

    return {
      partnerName: place?.partnerName,
      place: place?.locationAddress,
      doctorName: place?.doctorName,
      startsAt: this.startsAt(),
      // Only where it was answered: a collection has no end time of its own, and the half hour the
      // record is given so it has one is not something the manager said.
      endsAt: this.withEndTime ? spanEnd(this.startsAt(), this.endsAt()) : undefined,
      orderNumber: this.orderNumber().trim() || undefined,
    };
  });

  private readonly startsAt = computed(() => combine(this.date(), this.startTime()));
  private readonly endsAt = computed(() => endInstant(this.date(), this.startTime(), this.withEndTime ? this.endTime() : addMinutes(this.startTime(), DEFAULT_MINUTES)));

  /**
   * An order at a pharmacy is placed now and collected over the course it was prescribed for, so it
   * has a window rather than an hour. The window closes at the start where the course has no end or
   * has already run out: a record cannot be open for collection backwards.
   */
  private readonly collectionWindow = computed(() => {
    const from = new Date().toISOString();
    const until = this.prescription.details?.endDate;

    return { from, until: until && until > from ? until : from };
  });

  /** Whatever the source, the order is settled once it names where and when the care happens. */
  private placeAnswered(): boolean {
    if (this.binding()) {
      return !!this.candidate() || !!this.suggestion();
    }

    if (this.booking()) {
      if (this.ordersDrugs) {
        return !!this.basket();
      }

      return !this.needsDoctorStep() && !!this.slot();
    }

    const place = this.place();

    return !!place?.locationId && !!this.date() && !!this.startTime() && (!this.withDoctor || !!place.doctorName);
  }

  /**
   * A booking takes its terms from the partner or the record it names; one made by phone has only
   * what is typed here, so every line needs its price and the franchise has to be stated.
   */
  private termsAnswered(): boolean {
    if (!this.recording()) {
      return true;
    }

    return this.franchise() != null && this.priceRows().every(row => row.price != null && row.price > 0);
  }

  private stepCompleted(step: ExecutionStep): boolean {
    if (step === 'order') {
      return this.coveredIds().length > 0 && this.placeAnswered();
    }

    return step === 'terms' ? this.termsAnswered() : true;
  }

  protected goTo(step: ExecutionStep): void {
    this.step.set(step);
  }

  protected next(): void {
    const index = this.stepIndex();
    if (index < EXECUTION_STEPS.length - 1) {
      this.step.set(EXECUTION_STEPS[index + 1]);
    }
  }

  protected back(): void {
    const index = this.stepIndex();
    if (index > 0) {
      this.step.set(EXECUTION_STEPS[index - 1]);
    }
  }

  /**
   * Everything about where and when comes off the booking named — which is the whole point of
   * naming one rather than describing it again.
   *
   * What it costs is taken too, where the record carries a price: that is what was actually agreed
   * with the partner, and the line's reserved amount was only ever an estimate of it. The franchise
   * is not — what share the member pays was settled when the line was guaranteed, and a booking is
   * in no position to revise it.
   */
  /** A different doctor is a different schedule, so the hour taken from the last one cannot stand. */
  protected chooseDoctor(doctor: PersonalDoctorOption): void {
    this.doctor.set(doctor);
    this.slot.set(undefined);
  }

  protected clearDoctor(): void {
    this.doctor.set(undefined);
    this.slot.set(undefined);
  }

  protected chooseCandidate(candidate: PlanningCandidateModel): void {
    this.candidate.set(candidate);
    this.suggestion.set(undefined);
  }

  /** One visit answers the step, so taking the partner's drops the record of ours, and back. */
  protected chooseSuggestion(suggestion: PlanningSuggestionModel): void {
    this.suggestion.set(suggestion);
    this.candidate.set(undefined);
  }

  /** A different partner or branch is a different list, so what was taken from the last one cannot stand. */
  protected changeSuggestionPlace(place: PlaceChoice | undefined): void {
    this.suggestionPlace.set(place);
    this.suggestion.set(undefined);

    // Naming a partner narrows the list to it, so a record of another partner is no longer offered.
    if (place?.partnerId && this.candidate()?.partnerId !== place.partnerId) {
      this.candidate.set(undefined);
    }
  }

  protected priceOf(prescriptionId: string): number | null | undefined {
    return this.linePrices()[prescriptionId];
  }

  protected setPrice(prescriptionId: string, value: number | null | undefined): void {
    this.linePrices.update(current => ({ ...current, [prescriptionId]: value ?? null }));
  }

  protected setSource(source: ExecutionSource): void {
    this.source.set(source);

    // Each way of answering the place keeps only its own answer. An arrangement settled one way
    // states every part of itself, including the parts it leaves empty, so an answer given the
    // other way before the switch must not quietly fill those in.
    if (!this.binding()) {
      this.candidate.set(undefined);
      this.suggestion.set(undefined);
      this.suggestionPlace.set(undefined);
    }

    if (!this.booking()) {
      this.slot.set(undefined);
      this.basket.set(undefined);
      this.doctor.set(undefined);
    }

    if (!this.recording()) {
      this.place.set(undefined);
    }
  }

  /** The end follows the start unless it is set afterwards: one field answered instead of two. */
  protected setStart(value: string): void {
    this.startTime.set(value);
    this.endTime.set(addMinutes(value, DEFAULT_MINUTES));
  }

  protected toggleCovered(row: SiblingRow): void {
    if (row.blockerKey) {
      return;
    }

    const prescriptionId = row.line.prescription.id;

    this.coveredIds.update(current => (current.includes(prescriptionId) ? current.filter(id => id !== prescriptionId) : [...current, prescriptionId]));

    // What the partner offered was offered for a particular set of lines: an hour is long enough for
    // the services it was asked about, and a pharmacy holds the drugs it was asked for. Changing the
    // set makes that answer stale, so it is asked again rather than stretched to cover the change.
    if (this.booking()) {
      this.slot.set(undefined);
      this.basket.set(undefined);
    }
  }

  protected isCovered(prescriptionId: string): boolean {
    return this.coveredIds().includes(prescriptionId);
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    const covered = this.coveredIds();
    const considerPaidIds = this.considerPaid() ? covered : [];

    this.dialogRef.close(this.resultOf(covered, considerPaidIds));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  private resultOf(prescriptionIds: string[], considerPaidIds: string[]): ExecutionFormResult {
    if (this.binding() && this.suggestion()) {
      return { mode: 'book', prescriptionIds, booking: this.buildSuggestedBooking(), considerPaidIds };
    }

    if (this.binding()) {
      return { mode: 'bind', prescriptionIds, binding: this.buildBinding(), considerPaidIds };
    }

    if (this.booking()) {
      return { mode: 'book', prescriptionIds, booking: this.buildBooking(), considerPaidIds };
    }

    return { mode: 'record', prescriptionIds, record: this.buildRecord(), considerPaidIds };
  }

  /**
   * The binding as the record it names describes itself. Everything but the id and the terms is
   * read back off that record by the gateway, so what goes with it is only what the route insists
   * on — sent from the same entry rather than from the fields, which this source never fills.
   */
  private buildBinding(): BindPlanningRequest {
    const bound = this.candidate()!;

    const base = {
      userId: this.prescription.userId,
      partnerId: bound.partnerId,
      locationId: bound.locationId,
      prescriptions: this.prescriptionDetails(),
      startTime: bound.dateStart,
      // A pharmacy order is collected over a window that may have no stated end.
      endTime: bound.dateEnd ?? bound.dateStart,
    };

    if (this.shape === PrescriptionPlanningKind.Laboratory) {
      return { $type: 'laboratory', ...base, orderId: bound.id };
    }

    if (this.shape === PrescriptionPlanningKind.Drugs) {
      return { $type: 'drugs', ...base, orderId: bound.id };
    }

    return { $type: 'appointment', ...base, appointmentId: bound.id };
  }

  /**
   * The booking as the partner's own offer describes it, which is the only thing that makes it
   * placeable: an hour the partner published is one it will accept, and the products a pharmacy
   * listed are ones it has. Nothing here is typed, so nothing here can be off by a quarter of an
   * hour or name a drug the shelf does not hold.
   */
  private buildBooking(): BookPlanningRequest {
    const prescriptions = this.prescriptionDetails();

    if (this.ordersDrugs) {
      const chosen = this.basket()!;
      const window = this.collectionWindow();

      return {
        $type: 'drugs',
        source: PlanningSource.Slots,
        userId: this.prescription.userId,
        partnerId: chosen.pharmacy.partnerId,
        locationId: chosen.pharmacy.locationId,
        prescriptions,
        startTime: window.from,
        endTime: window.until,
        products: chosen.products,
      };
    }

    const taken = this.slot()!;

    const base = {
      source: PlanningSource.Slots as const,
      userId: this.prescription.userId,
      partnerId: taken.offer.partnerId,
      locationId: taken.offer.locationId,
      prescriptions,
      startTime: taken.slot.startTime,
      endTime: taken.slot.endTime,
    };

    if (this.shape === PrescriptionPlanningKind.Laboratory) {
      return { $type: 'laboratory', ...base };
    }

    return { $type: 'appointment', ...base, doctorId: taken.offer.doctorId, eventPlace: eventPlaceOf(taken.slot) };
  }

  /**
   * The partner's visit taken as it stands. Where, who and when go back exactly as the partner
   * answered them: that is how the gateway finds the same visit again, and nothing new is booked.
   */
  private buildSuggestedBooking(): BookPlanningRequest {
    const suggested = this.suggestion()!;

    return {
      $type: 'appointment',
      source: PlanningSource.Suggestions,
      userId: this.prescription.userId,
      partnerId: suggested.partnerId,
      locationId: suggested.locationId,
      prescriptions: this.prescriptionDetails(),
      startTime: suggested.startDate,
      endTime: suggested.endDate,
      doctorId: suggested.doctorId,
      eventPlace: AppointmentEventPlace.Hospital,
    };
  }

  /** Each covered line with the terms it is arranged on: one franchise for all, a price of its own. */
  private prescriptionDetails(): PlanningPrescriptionDetails[] {
    const franchise = this.franchise();

    return this.coveredIds().map(id => ({ id, franchise, price: this.priceOf(id) ?? undefined }));
  }

  /** The arrangement as the manager described it. */
  private buildRecord(): RecordPlanningRequest {
    const place = this.place()!;

    const base = {
      source: PlanningSource.Manual as const,
      userId: this.prescription.userId,
      partnerId: place.partnerId,
      locationId: place.locationId!,
      prescriptions: this.prescriptionDetails(),
      startTime: this.startsAt()!,
      endTime: this.endsAt()!,
    };

    if (this.shape === PrescriptionPlanningKind.Appointment) {
      return { $type: 'appointment', ...base, doctorName: place.doctorName! };
    }

    const orderNumber = this.orderNumber().trim() || undefined;

    return this.shape === PrescriptionPlanningKind.Laboratory ? { $type: 'laboratory', ...base, orderNumber } : { $type: 'drugs', ...base, orderNumber };
  }
}

/** Local wall-clock in, instant out: what the manager typed is the time at the partner. */
function combine(date: string, time: string): string | undefined {
  if (!date || !time) {
    return undefined;
  }

  const parsed = new Date(`${date}T${time}`);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The end read forward from the start, so it never lands before it. A slot that reads earlier on the
 * clock than it began — a visit at 23:50, or a default half-hour that ran past midnight — is the
 * next day, which is what the clock means rather than a record with a negative length.
 */
function endInstant(date: string, start: string, end: string): string | undefined {
  const startsAt = combine(date, start);
  const endsAt = combine(date, end);
  if (!startsAt || !endsAt) {
    return undefined;
  }

  return endsAt < startsAt ? new Date(Date.parse(endsAt) + DAY_MS).toISOString() : endsAt;
}

function addMinutes(time: string, minutes: number): string {
  const [hours, mins] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(mins)) {
    return time;
  }

  const total = (hours * 60 + mins + minutes) % (24 * 60);

  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Money, to the kopiyka: a price list answers with more places than a sum can be paid in. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
